import { Injectable } from '@angular/core';
import { AsyncSubject, Observable } from 'rxjs';
import { delay, filter, finalize, map, mergeMap } from 'rxjs/operators';

import {
  DistinctSubject,
  NeverError,
  ProgressUsecase,
  WebSocketSyncData,
  WebSocketUsecase,
  recursiveQuery,
} from '@daikin-tic/dxone-com-lib';

import {
  Checklist,
  ChecklistCondition,
  ChecklistConditionsCreateParams,
  ChecklistCreateParams,
  ChecklistItem,
  ChecklistItemsCreateParams,
  ChecklistLink,
  Checklists,
} from '../models/checklist.model';
import { ChecklistGateway } from '../usecases/checklist.gateway';
import { ChecklistUsecase } from '../usecases/checklist.usecase';
import { CloudStorageGateway } from '../usecases/cloud-storage.gateway';

const UPLOAD_DELAY = 10 * 1000;

@Injectable()
export class ChecklistInteractor extends ChecklistUsecase {
  get checklists$(): Observable<Checklists> {
    return this._checklists.pipe(
      map(checklists =>
        checklists.values().map(checklist => ({
          ...checklist,
          checklistLinks$: this.listChecklistLinks(checklist.checklistId),
          checklistFlowchart$: this.getChecklistFlowchart(checklist.checklistId),
          checklistItems$: this.listChecklistItems(checklist.checklistId),
          checklistConditions$: this.listChecklistConditions(checklist.checklistId),
        })),
      ),
      map(checklists => new Checklists(checklists)),
    );
  }

  private readonly _checklists = new DistinctSubject<Checklists>(new Checklists());

  constructor(
    private _progressUsecase: ProgressUsecase,
    private _webSocketUsecase: WebSocketUsecase,
    private _checklistGateway: ChecklistGateway,
    private _cloudStorageGateway: CloudStorageGateway,
  ) {
    super();
    this._webSocketUsecase.isOpen$.subscribe(isOpen => (isOpen ? this.onSignIn() : this.onSignOut()));
    this._webSocketUsecase.message$
      .pipe(
        filter(message => message.action === 'sync' && message.data?.source === 'checklist'),
        map(({ data }) => data as WebSocketSyncData<Checklist>),
      )
      .subscribe(data => {
        switch (data.reason) {
          case 'create':
          case 'update':
            this._checklists.next(this._checklists.value.set(data.payload));
            break;
          case 'delete':
            this._checklists.next(this._checklists.value.delete((data.payload as Checklist).checklistId));
            break;
          default:
            throw new NeverError(data.reason);
        }
      });
  }

  createChecklist(params: ChecklistCreateParams): Observable<string> {
    const result = new AsyncSubject<string>();
    this._checklistGateway.createChecklist(params).subscribe({
      next: createdChecklist => {
        this._checklists.next(this._checklists.value.set(createdChecklist));
        result.next(createdChecklist.checklistId);
      },
      error: result.error.bind(result),
      complete: result.complete.bind(result),
    });
    return result.asObservable();
  }

  deleteChecklist(checklistId: string): Observable<never> {
    const result = new AsyncSubject<never>();
    this._checklistGateway.deleteChecklist(checklistId).subscribe({
      next: () => this._checklists.next(this._checklists.value.delete(checklistId)),
      error: result.error.bind(result),
      complete: result.complete.bind(result),
    });
    return result.asObservable();
  }

  createChecklistFlowchart(checklistId: string, flowchart: string): Observable<never> {
    const progressId = this._progressUsecase.show();
    const result = new AsyncSubject<never>();
    const blob = new Blob([flowchart], { type: 'text/plain; charset=utf-8' });
    this._checklistGateway
      .createChecklistFlowchart(checklistId)
      .pipe(
        mergeMap(storage => this._cloudStorageGateway.upload(storage, blob)),
        delay(UPLOAD_DELAY),
        finalize(() => this._progressUsecase.dismiss(progressId)),
      )
      .subscribe({
        error: result.error.bind(result),
        complete: result.complete.bind(result),
      });
    return result.asObservable();
  }

  createChecklistItems(checklistId: string, params: ChecklistItemsCreateParams): Observable<never> {
    const result = new AsyncSubject<never>();
    this._checklistGateway.createChecklistItems(checklistId, params).subscribe({
      error: result.error.bind(result),
      complete: result.complete.bind(result),
    });
    return result.asObservable();
  }

  createChecklistConditions(checklistId: string, params: ChecklistConditionsCreateParams): Observable<never> {
    const result = new AsyncSubject<never>();
    this._checklistGateway.createChecklistConditions(checklistId, params).subscribe({
      error: result.error.bind(result),
      complete: result.complete.bind(result),
    });
    return result.asObservable();
  }

  private onSignIn(): void {
    recursiveQuery(params => this._checklistGateway.listChecklists(params), {}).subscribe(checklists => {
      this._checklists.next(new Checklists(checklists));
    });
  }

  private onSignOut(): void {
    this._checklists.next(new Checklists());
  }

  private listChecklistLinks(checklistId: string): Observable<ChecklistLink[]> {
    return recursiveQuery(params => this._checklistGateway.listChecklistLinks(checklistId, params), {}, true);
  }

  private getChecklistFlowchart(checklistId: string): Observable<string> {
    return this._checklistGateway.getChecklistFlowchart(checklistId);
  }

  private listChecklistItems(checklistId: string): Observable<ChecklistItem[]> {
    return recursiveQuery(params => this._checklistGateway.listChecklistItems(checklistId, params), {}, true);
  }

  private listChecklistConditions(checklistId: string): Observable<ChecklistCondition[]> {
    return recursiveQuery(params => this._checklistGateway.listChecklistConditions(checklistId, params), {}, true);
  }
}
